import { DialogComponent } from '@theme/dialog';

/**
 * Story popup — a full-screen dialog with "Story" and "Playlist" tabs.
 *
 * Extends the theme's DialogComponent so it inherits modal open/close,
 * scroll-locking and escape/outside-click handling, and adds tab switching.
 *
 * Markup contract:
 *   - Tab triggers carry `data-story-tab="story|playlist"` and `on:click="/selectTab"`.
 *   - Panels carry `data-story-panel="story|playlist"`.
 *   - The host element reflects the active tab via `data-active-tab` (drives theming).
 */
class StoryPopupComponent extends DialogComponent {
  /**
   * The story dialog only renders when the collection has `story_details`
   * (e.g. collections/all has none). DialogComponent marks `dialog` as a required
   * ref and throws when it's absent, which would abort connectedCallback before the
   * banner's scrollspy + title-collapse initialise. Those don't need the dialog, so
   * make the ref optional here; the dialog methods are only ever triggered by buttons
   * that render alongside the dialog.
   *
   * @type {string[]}
   */
  requiredRefs = [];

  connectedCallback() {
    super.connectedCallback();
    this.setActiveTab(this.dataset.activeTab || 'story');
    this.#initTrackScrollspy();
    this.#initTitleCollapse();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.onTrackScroll) {
      window.removeEventListener('scroll', this.onTrackScroll);
      window.removeEventListener('resize', this.onTrackScroll);
    }
    if (this.onTitleScroll) {
      window.removeEventListener('scroll', this.onTitleScroll);
    }
  }

  /**
   * Opens the dialog on the Story tab (default trigger).
   */
  showDialog() {
    this.setActiveTab('story');
    super.showDialog();
  }

  /**
   * Opens the dialog on the Playlist tab.
   */
  showPlaylist() {
    this.setActiveTab('playlist');
    super.showDialog();
  }

  /**
   * Switches the active tab from a trigger click.
   *
   * @param {Event} event - The click event.
   */
  selectTab = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-story-tab]') : null;
    const tab = target instanceof HTMLElement ? target.dataset.storyTab : undefined;

    if (tab) this.setActiveTab(tab);
  };

  /**
   * Smooth-scrolls the page to the in-grid track banner whose `data-track-index`
   * matches the clicked track button's `data-track-index`.
   *
   * Scrolls the window explicitly rather than via `scrollIntoView`: the grid's
   * `<results-list>` ancestor is an `overflow: auto` scroll container, so
   * `scrollIntoView` resolves to it and never scrolls the page. The scroll is instant,
   * not smooth — Klaviyo's `prevent-body-scrolling` lock plus GSAP's pinned banners
   * cancel per-frame/native smooth scrolling on this store, so a single jump is the only
   * reliable move. The active state is NOT set here — it is driven by scroll position
   * (see the scrollspy below).
   *
   * @param {Event} event - The click event.
   */
  scrollToTrack = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-track-index]') : null;
    if (!(button instanceof HTMLElement)) return;

    const index = button.getAttribute('data-track-index');
    if (!index) return;

    // Track 1 is the main grid's top anchor; Tracks 2+ are the next-drop banners. Target the
    // matching track point. Next-drop banners only enter the DOM once infinite scroll has
    // loaded the last page — if the target isn't present yet, jump to the bottom of the grid
    // so it loads, and the shopper can tap again once it's in.
    const point = document.querySelector(`.collection-track-point[data-track-index="${index}"]`);
    if (!(point instanceof HTMLElement) || point.offsetParent === null) {
      const grid = document.querySelector('.collection-product-grid');
      if (grid instanceof HTMLElement) {
        window.scrollTo({ top: grid.getBoundingClientRect().bottom + window.scrollY });
      }
      return;
    }

    const top = this.#trackTarget(point).getBoundingClientRect().top + window.scrollY;
    window.scrollTo({ top });
  };

  /**
   * The element whose top marks a track point's region start. Track 1's anchor is rendered at
   * the top of whichever page the server loaded first; when that's a later page (infinite
   * scroll pushes `?page=N`, so a reload or Back lands there), earlier pages are prepended
   * above it. The grid itself is the true start of the main track.
   *
   * @param {HTMLElement} point - A `.collection-track-point`.
   * @returns {Element}
   */
  #trackTarget(point) {
    if (!point.classList.contains('collection-track-anchor')) return point;
    return point.closest('.collection-product-grid') ?? point;
  }

  /**
   * Collapses the banner title once the page is scrolled past a small threshold.
   * Adds/removes `title--collapsed` on `.track-banner__info` when the window is
   * scrolled more than 200px down from the top. rAF-throttled to stay cheap.
   */
  #initTitleCollapse() {
    const info = this.querySelector('aside.track-banner');
    if (!info) return;

    const COLLAPSE_THRESHOLD = 200;

    const updateTitleCollapsed = () => {
      info.classList.toggle('title--collapsed', window.scrollY > COLLAPSE_THRESHOLD);
    };

    this.onTitleScroll = () => {
      if (this.titleScrollTicking) return;
      this.titleScrollTicking = true;
      requestAnimationFrame(() => {
        updateTitleCollapsed();
        this.titleScrollTicking = false;
      });
    };

    window.addEventListener('scroll', this.onTitleScroll, { passive: true });
    updateTitleCollapsed();
  }

  /**
   * Scrollspy: keeps the active track button in sync with the banner the viewport is
   * currently within. A button is active only while its banner crosses the viewport's
   * vertical centre — none is active while the shopper is on product rows.
   *
   * Implemented with a window scroll listener + getBoundingClientRect rather than an
   * IntersectionObserver: the grid's `<results-list>` ancestor is an `overflow: auto`
   * container, and getBoundingClientRect reports true on-screen position regardless of
   * such clipping, on the window scroller the page actually uses.
   */
  #initTrackScrollspy() {
    if (document.querySelector('.collection-track-point[data-track-index]') === null) return;

    this.updateActiveTrack = () => {
      // Track points are region starts: the top of the grid for Track 1, then each
      // next-drop banner. Re-query every tick because next-drop points are appended lazily by
      // infinite scroll. The active track is the last point whose top has passed the viewport
      // midpoint — i.e. the region the shopper is currently within.
      const points = document.querySelectorAll('.collection-track-point[data-track-index]');
      const midpoint = window.innerHeight / 2;
      let activeIndex = null;

      for (const point of points) {
        if (!(point instanceof HTMLElement) || point.offsetParent === null) continue;
        if (this.#trackTarget(point).getBoundingClientRect().top <= midpoint) {
          activeIndex = point.getAttribute('data-track-index');
        }
      }

      this.#reflectActiveTrack(activeIndex);
    };

    // rAF-throttle the scroll handler.
    this.onTrackScroll = () => {
      if (this.trackScrollTicking) return;
      this.trackScrollTicking = true;
      requestAnimationFrame(() => {
        this.updateActiveTrack();
        this.trackScrollTicking = false;
      });
    };

    window.addEventListener('scroll', this.onTrackScroll, { passive: true });
    window.addEventListener('resize', this.onTrackScroll, { passive: true });
    this.updateActiveTrack();
  }

  /**
   * Applies the active state to the track button matching the currently-centred banner,
   * or clears all buttons when none is centred.
   *
   * @param {string | null} activeIndex - The `data-track-index` to activate, or null.
   */
  #reflectActiveTrack(activeIndex) {
    for (const btn of this.querySelectorAll('.track-banner__tracks-buttons button')) {
      const isActive = activeIndex !== null && btn.getAttribute('data-track-index') === activeIndex;
      btn.classList.toggle('is-active', isActive);
      if (isActive) {
        btn.setAttribute('aria-current', 'true');
      } else {
        btn.removeAttribute('aria-current');
      }
    }
  }

  /**
   * Roving-tabindex keyboard navigation for the tablist. The bar is duplicated inside each
   * panel, so scope everything to the tablist the event came from (the delegated handler
   * proxies `event.target` to the element carrying `on:keydown`, i.e. the tablist) and use
   * `document.activeElement` for the current tab.
   *
   * @param {KeyboardEvent} event - The keydown event.
   */
  handleTabKeydown = (event) => {
    const tablist = event.target instanceof Element ? event.target.closest('[role="tablist"]') : null;
    if (!(tablist instanceof HTMLElement)) return;

    const tabs = Array.from(tablist.querySelectorAll('[data-story-tab]'));
    if (tabs.length === 0) return;

    // The tab triggers are anchors with role="button", so replicate a button's
    // Enter/Space activation (a native <button> did this for free).
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      const focused = tabs.find((tab) => tab === document.activeElement) ?? tabs[0];
      if (focused instanceof HTMLElement) {
        event.preventDefault();
        this.setActiveTab(focused.dataset.storyTab ?? 'story');
        this.#focusVisibleTab(focused.dataset.storyTab ?? 'story');
      }
      return;
    }

    const keys = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];
    if (!keys.includes(event.key)) return;

    let current = tabs.findIndex((tab) => tab === document.activeElement);
    if (current === -1) current = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    if (current === -1) current = 0;

    let next = current;
    if (event.key === 'ArrowRight') next = (current + 1) % tabs.length;
    if (event.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = tabs.length - 1;

    const nextTab = tabs[next];
    if (!(nextTab instanceof HTMLElement)) return;

    event.preventDefault();
    const nextTabName = nextTab.dataset.storyTab ?? 'story';
    this.setActiveTab(nextTabName);
    // Switching hides this panel (and its bar), so move focus to the matching tab in the
    // panel that just became visible.
    this.#focusVisibleTab(nextTabName);
  };

  /**
   * Focuses the tab for `tab` inside the panel that is currently visible for it.
   *
   * @param {string} tab - The tab id ("story" or "playlist").
   */
  #focusVisibleTab(tab) {
    const panel = this.querySelector(`[data-story-panel="${tab}"]`);
    const target = panel?.querySelector(`[data-story-tab="${tab}"]`);
    if (target instanceof HTMLElement) target.focus();
  }

  /**
   * Activates a tab and reveals its matching panel.
   *
   * @param {string} tab - The tab id ("story" or "playlist").
   */
  setActiveTab(tab) {
    if (!tab) return;

    this.dataset.activeTab = tab;

    for (const trigger of this.querySelectorAll('[data-story-tab]')) {
      const isActive = trigger instanceof HTMLElement && trigger.dataset.storyTab === tab;
      trigger.classList.toggle('is-active', isActive);
      trigger.setAttribute('aria-selected', isActive ? 'true' : 'false');
      trigger.setAttribute('tabindex', isActive ? '0' : '-1');
    }

    for (const panel of this.querySelectorAll('[data-story-panel]')) {
      const isActive = panel instanceof HTMLElement && panel.dataset.storyPanel === tab;
      panel.classList.toggle('is-active', isActive);
      panel.toggleAttribute('hidden', !isActive);
    }
  }
}

if (!customElements.get('story-popup')) {
  customElements.define('story-popup', StoryPopupComponent);
}
